import {
  BottomSheetContainer,
  createUseBottomSheetModal,
  Flex,
  useSafeArea,
  useScreenSize,
} from '@banhala/ui-kit';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SelectOptionView } from './components/SelectOptionView';
import { InlineOptionView } from './components/InlineOptionView';
import type { GoodsOption } from 'shared/src/api';
import { PricingListView } from './components/ProductListView';
import { SelectOptionsContext } from './context/SelectOptionsContext';
import { useLogViewGoodsOptionModal } from './hooks/useLogViewGoodsOptionModal';
import { bridge, isNotNil } from 'shared/src/utils';
import { systemAlert, useMemberSignupRequestModal } from 'shared/src/component';
import { useLogGoodsAnalyticsEvent } from '../../hooks/useLogGoodsAnalyticsEvent';
import { useInlineOptionMode } from '../../hooks/useInlineOptionMode';
import { useProductOptionMaxDiscountMode } from '../../hooks/useProductOptionMaxDiscountMode';
import { useGoodsOptionsBrief } from './queries/useGoodsOptionsBrief';

const INLINE_HEIGHT_SINGLE = 390;
const INLINE_HEIGHT_MULTI = 564;

export type SelectOptionConfirm = {
  lastAction: 'checkout' | 'addToCart' | 'close';
};

export type SelectOptionParam = {
  mode: 'purchase' | 'gift';
  isShowOnce: boolean;
  selectedOptions: GoodsOption[];
  updateSelectedOption: (options: GoodsOption[]) => void;
  fromButton?: string;
};

export const SelectOptionBottomSheetContent = ({
  param,
  confirm,
}: {
  param: SelectOptionParam;
  confirm: (data: SelectOptionConfirm) => void;
}) => {
  const { height: screenHeight } = useScreenSize();
  const { show: showMemberSignupRequestModal } = useMemberSignupRequestModal();

  const { top, bottom } = useSafeArea();
  const [type, setType] = useState<'select' | 'pricing'>(
    param.selectedOptions.length > 0 ? 'pricing' : 'select',
  );
  const [selectedOptions, setSelectedOptions] = useState<GoodsOption[]>(
    param.selectedOptions,
  );
  const logGoodsAnalytics = useLogGoodsAnalyticsEvent();
  const inlineMode = useInlineOptionMode({ enabled: param.mode !== 'gift' });
  const isInline = inlineMode === 'inline';
  const maxDiscountMode = useProductOptionMaxDiscountMode({
    enabled: param.mode !== 'gift',
  });
  const { data: brief } = useGoodsOptionsBrief();

  const isAllSingleOption = useMemo(() => {
    if (!brief) return false;
    return brief.option_groups.every(g => g.component_count === 1);
  }, [brief]);

  const bottomSheetHeight = useMemo(() => {
    const fallbackHeight = (screenHeight - top - bottom) * 0.8;
    // brief 는 GoodsDetail 진입 시 prefetch 되므로, 적격 상품은 첫 렌더부터 inlineMode 가 확정돼
    // 인라인 높이가 즉시 적용된다. prefetch 가 없으면 standard → fallback 후 brief 로드 시 갱신.
    if (inlineMode === 'standard') return fallbackHeight;
    return isAllSingleOption ? INLINE_HEIGHT_SINGLE : INLINE_HEIGHT_MULTI;
  }, [bottom, inlineMode, isAllSingleOption, screenHeight, top]);
  useLogViewGoodsOptionModal(param);

  const isMountedRef = useRef(false);
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    param.updateSelectedOption(selectedOptions);
  }, [selectedOptions, param]);

  return (
    <BottomSheetContainer showHandleBar={isInline} hideBorder={isInline}>
      <SelectOptionsContext.Provider value={{ mode: param.mode }}>
        <Flex height={bottomSheetHeight}>
          {type === 'select' && isInline && (
            <InlineOptionView
              maxDiscountMode={maxDiscountMode}
              onComplete={action => {
                if (action === 'addToCart') {
                  setSelectedOptions([]);
                }
                confirm({ lastAction: action });
              }}
            />
          )}
          {type === 'select' && !isInline && (
            <SelectOptionView
              onSelectOptionComplete={option => {
                setSelectedOptions(prev => {
                  const sameOption = prev.find(
                    prevOption => prevOption.sno === option.sno,
                  );
                  if (isNotNil(sameOption)) {
                    const currentCount = sameOption.purchase_count ?? 1;
                    const maxLimit = isNotNil(option.max_order_limit_count)
                      ? Math.min(option.stock, option.max_order_limit_count)
                      : option.stock;

                    if (currentCount >= maxLimit) {
                      systemAlert(
                        `${maxLimit}개까지 구매 가능한 옵션이에요`,
                        `${option.option_names.join('/')} 옵션은 인당 최대 ${maxLimit}개까지 구매할 수 있어요.`,
                        [{ text: '확인' }],
                      );
                      return prev;
                    }

                    return prev.map(prevOption => {
                      if (prevOption.sno === option.sno) {
                        return {
                          ...prevOption,
                          purchase_count: currentCount + 1,
                        };
                      }
                      return prevOption;
                    });
                  }

                  return [
                    {
                      ...option,
                      purchase_count: Math.max(option.purchase_count ?? 0, 1),
                    },
                    ...prev,
                  ];
                });
                setType('pricing');
              }}
              onPressClose={() => {
                if (selectedOptions.length === 0) {
                  confirm({ lastAction: 'close' });
                } else {
                  setType('pricing');
                }
              }}
            />
          )}
          {type === 'pricing' && (
            <PricingListView
              selected={selectedOptions}
              maxDiscountMode={maxDiscountMode}
              onCompleteAddToCart={() => {
                setSelectedOptions([]);
                confirm({ lastAction: 'addToCart' });
              }}
              onCompletePurchase={() => {
                confirm({ lastAction: 'checkout' });
              }}
              onUpdateOption={option => {
                setSelectedOptions(prev =>
                  prev.map(prevOption => {
                    if (prevOption.sno === option.sno) {
                      return {
                        ...prevOption,
                        ...option,
                      };
                    }
                    return prevOption;
                  }),
                );
              }}
              onSelectMore={() => setType('select')}
              onPressDelete={option => {
                setSelectedOptions(prev =>
                  prev.filter(prevOption => prevOption.sno !== option.sno),
                );
              }}
              onAnonymouseUserPressedPurchase={() => {
                logGoodsAnalytics({
                  name: 'VIEW_POPUP_ORDER_LIMIT',
                });
                showMemberSignupRequestModal({
                  title: '',
                  description: '지금 가입하고\n더 많은 상품을 만나보세요',
                }).then(result => {
                  if (result.type === 'confirm') {
                    bridge.assertLogin({});
                  }
                });
              }}
            />
          )}
        </Flex>
      </SelectOptionsContext.Provider>
    </BottomSheetContainer>
  );
};

export const useSelectOptionBottomSheet = createUseBottomSheetModal<
  SelectOptionConfirm,
  SelectOptionParam
>(({ param, confirm }) => (
  <SelectOptionBottomSheetContent param={param} confirm={confirm} />
));
